const noPreviousValue = Symbol();

/**
 * A class that checks if a value is consistently returning the same value (referentially) in between checks.
 *
 * The purpose of this (on July 13, 2022) is because of how `transformAsyncInputDataToAsyncState` handles
 * finding the selected value in the list of options, if the options array is consistently changed in between
 * renders, we will see a huge performance hit.
 */
export class EqualityCheckCounter<T> {
  private count = 0;
  private warningTriggered = false;
  private prev: T | typeof noPreviousValue = noPreviousValue;

  constructor(private warn: () => void, private maxCount: number) {}

  reset(): void {
    this.innerReset(noPreviousValue);
  }

  check(next: T): void {
    if (this.warningTriggered) {
      return;
    }

    /**
     * If the data is ready, then we have data. If the previous data ref has not been set or the
     * current data is equal to what we previously saw, then we can reset everything.
     */
    if (this.prev === next) {
      this.innerReset(next);
      return;
    }

    /**
     * Increment the counter if the previous value is not noPreviousValue.
     */
    this.count += Number(this.prev !== noPreviousValue);
    this.prev = next;

    /**
     * If the data is not the same, then we may have a problem.
     * We will increment the counter, but we will not warn the developer until this has happened
     * at least _maxCount checks in a row_.
     */
    if (this.count >= this.maxCount) {
      this.warningTriggered = true;
      this.warn();
    }
  }

  private innerReset(value: T | typeof noPreviousValue): void {
    this.count = 0;
    this.warningTriggered = false;
    this.prev = value;
  }
}
